import {
  endCognitoSession,
  errorEndsTheSession,
  otherIdentityKeys,
  readLoginContext,
  sessionEndedLoginPath,
} from './session-end';
import { AuthProvider } from '@refinedev/core';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  type IAuthenticationCallback,
} from 'amazon-cognito-identity-js';
import { adminFetch } from './admin-api';
import { currentFreshSession } from './session-refresh';

const apiUrl = import.meta.env.VITE_API_URL || '';

/**
 * Best-effort POST to /v1/admin/audit/session-event so every Cognito
 * login + logout writes an audit row. Both fire-and-forget — the
 * onSuccess / signOut path is the user-facing one and must not block on
 * audit. Errors are swallowed silently (the request is already logged
 * by the lambda on 4xx/5xx if it ever reaches the API).
 */
async function recordAdminSessionEvent(event: 'login' | 'logout'): Promise<void> {
  try {
    await adminFetch(`${apiUrl}/v1/admin/audit/session-event`, {
      method: 'POST',
      body: JSON.stringify({ event_type: event }),
    });
  } catch {
    // Swallow — audit best-effort.
  }
}

// Cognito configuration from environment variables
const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID || '';
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || '';

const userPool = new CognitoUserPool({
  UserPoolId: userPoolId,
  ClientId: clientId,
});

/**
 * #2936: remove the stored Cognito keys of every identity of this app client other
 * than `currentUser` (of all of them when it is null), so no earlier admin's refresh
 * token stays in a shared browser. The rule is `otherIdentityKeys` (session-end.ts).
 */
function removeOtherIdentities(currentUser: string | null): void {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key !== null) keys.push(key);
  }
  for (const key of otherIdentityKeys(keys, userPool.getClientId(), currentUser)) localStorage.removeItem(key);
}

// Holds the in-flight Cognito challenge between the initial login() (which
// surfaces it) and the step that resolves it (completeNewPassword /
// completeMfaSetup / submitMfaCode). First-time invitees land in
// FORCE_CHANGE_PASSWORD; the dedicated admin pool (admin-identity-plane Phase 1)
// also requires TOTP MFA, so a fresh admin walks: password → new password →
// TOTP enrollment (scan QR) → done; a returning admin walks: password → TOTP
// code → done.
let pendingChallengeUser: CognitoUser | null = null;
let pendingChallengeEmail: string | null = null;
// Set during an MFA_SETUP challenge so the LoginPage can render the enrollment
// QR / secret. Cleared once setup completes.
let pendingMfaSecret: string | null = null;
let pendingMfaOtpauthUri: string | null = null;

type LoginResult = {
  success: boolean;
  redirectTo?: string;
  error?: { name: string; message: string };
};

// Issuer label shown in the user's authenticator app (env-suffixed off prod).
const MFA_ENV = (import.meta.env as Record<string, string | undefined>).VITE_ENV;
const MFA_ISSUER = `UDC Admin${MFA_ENV && MFA_ENV !== 'prod' ? ` (${MFA_ENV})` : ''}`;

function finishLogin(email: string, resolve: (r: LoginResult) => void): void {
  pendingChallengeUser = null;
  pendingChallengeEmail = null;
  pendingMfaSecret = null;
  pendingMfaOtpauthUri = null;
  if (email) localStorage.setItem('admin_email', email);
  // Session is established by amazon-cognito-identity-js (auto-persisted to
  // localStorage, which also sets LastAuthUser); fire the login audit
  // best-effort and proceed.
  recordAdminSessionEvent('login');
  // #2936: no earlier identity's keys stay behind. Only when the new identity
  // is known — a null here would remove the session just established.
  const signedIn = userPool.getCurrentUser()?.getUsername();
  if (signedIn) removeOtherIdentities(signedIn);
  // Back to the page the operator was on when the session ended, when the
  // login URL names a safe internal one; the dashboard otherwise.
  const returnTo = readLoginContext(window.location.search).returnTo;
  resolve({ success: true, redirectTo: returnTo ?? '/dashboard' });
}

/**
 * The single source of truth for handling EVERY Cognito challenge the admin
 * pool can surface. Reused by login(), completeNewPassword(), submitMfaCode()
 * and selectMFAType so that challenges chain correctly (e.g. new-password →
 * MFA-setup). Each branch stashes the in-flight cognitoUser + resolves the
 * caller's promise with a named error the LoginPage maps to the next step.
 */
function makeChallengeCallbacks(
  cognitoUser: CognitoUser,
  email: string,
  resolve: (r: LoginResult) => void,
): IAuthenticationCallback {
  const onFailure = (err: { message?: string }) =>
    resolve({
      success: false,
      error: { name: 'LoginError', message: err?.message || 'Authentication failed' },
    });
  return {
    onSuccess: () => finishLogin(email, resolve),
    onFailure,
    newPasswordRequired: () => {
      // Immutable attrs (email/email_verified) are rejected on the challenge
      // response, so completeNewPassword passes {}.
      pendingChallengeUser = cognitoUser;
      pendingChallengeEmail = email;
      resolve({ success: false, error: { name: 'NewPasswordRequired', message: 'NEW_PASSWORD_REQUIRED' } });
    },
    totpRequired: () => {
      // Returning admin with TOTP already enrolled.
      pendingChallengeUser = cognitoUser;
      pendingChallengeEmail = email;
      resolve({ success: false, error: { name: 'SoftwareTokenMfaRequired', message: 'SOFTWARE_TOKEN_MFA' } });
    },
    mfaRequired: () => {
      // SMS MFA — never configured on the admin pool (TOTP-only). Fail clearly
      // rather than attempt an SMS the pool can't send.
      resolve({
        success: false,
        error: { name: 'SmsMfaUnsupported', message: 'SMS MFA is not supported for admin accounts.' },
      });
    },
    selectMFAType: () => {
      // Only TOTP is enabled; answer it and continue with the same handlers.
      cognitoUser.sendMFASelectionAnswer(
        'SOFTWARE_TOKEN_MFA',
        makeChallengeCallbacks(cognitoUser, email, resolve),
      );
    },
    mfaSetup: () => {
      // First login with MFA required + no authenticator yet: associate a TOTP
      // secret, surface it for the QR, and wait for the user's first code.
      cognitoUser.associateSoftwareToken({
        associateSecretCode: (secretCode: string) => {
          pendingChallengeUser = cognitoUser;
          pendingChallengeEmail = email;
          pendingMfaSecret = secretCode;
          pendingMfaOtpauthUri =
            `otpauth://totp/${encodeURIComponent(MFA_ISSUER)}:${encodeURIComponent(email)}` +
            `?secret=${secretCode}&issuer=${encodeURIComponent(MFA_ISSUER)}`;
          resolve({ success: false, error: { name: 'MfaSetupRequired', message: 'MFA_SETUP' } });
        },
        onFailure,
      });
    },
  };
}

// Get current session — refreshed when it has under a minute left, so a request
// never reaches the API with a token that expires on the way (session-refresh.ts).
const getCurrentSession = (): Promise<CognitoUserSession | null> =>
  currentFreshSession<CognitoUserSession>(userPool.getCurrentUser());

// Get ID token for API calls
export const getIdToken = async (): Promise<string | null> => {
  const session = await getCurrentSession();
  if (!session) return null;
  return session.getIdToken().getJwtToken();
};

// Auth provider for Refine
/** Set by the 401 path; read by `check()`. Page-lifetime only, never stored. */
let sessionEndedByApiError = false;

export const authProvider: AuthProvider = {
  login: async ({ email, password }) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    return new Promise<LoginResult>((resolve) => {
      // All challenge handling (incl. TOTP MFA on the admin pool) flows through
      // the shared factory so new-password → MFA-setup chains correctly.
      cognitoUser.authenticateUser(authDetails, makeChallengeCallbacks(cognitoUser, email, resolve));
    });
  },

  logout: async () => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      // Fire logout audit BEFORE signOut so the ID token is still valid
      // for the API call. We await this one (capped by adminFetch's
      // fast-fail on missing token) so the audit row reliably lands
      // before the redirect.
      await recordAdminSessionEvent('logout');
      // #2936: revoke the refresh token at Cognito, then clear this browser
      // (endCognitoSession clears it locally if revocation cannot run).
      await endCognitoSession(cognitoUser);
    }
    // No identity of this app client stays in the browser after a sign-out.
    removeOtherIdentities(null);
    localStorage.removeItem('admin_email');
    return {
      success: true,
      redirectTo: '/login',
    };
  },

  check: async () => {
    const session = await getCurrentSession();
    if (session) {
      return {
        authenticated: true,
      };
    }
    // No session on a protected page: the 24-hour admin session is over (or
    // there never was one). Say so on the login page, and remember the page.
    return {
      authenticated: false,
      redirectTo: sessionEndedLoginPath(
        window.location.pathname,
        window.location.search,
        // Written at sign-in, removed by an EXPLICIT sign-out, left by an expiry.
        // OR a 401 just ended the session: that path signs out (which removes
        // the marker) BEFORE the layout asks `check()` again, and whichever
        // redirect lands last must still carry the reason.
        localStorage.getItem('admin_email') !== null || sessionEndedByApiError,
      ),
    };
  },

  getPermissions: async () => {
    const session = await getCurrentSession();
    if (!session) return null;

    // Get groups from ID token
    const idToken = session.getIdToken();
    const payload = idToken.decodePayload();
    const groups = payload['cognito:groups'] || [];

    return groups;
  },

  getIdentity: async () => {
    const session = await getCurrentSession();
    if (!session) return null;

    const idToken = session.getIdToken();
    const payload = idToken.decodePayload();

    return {
      id: payload.sub,
      name: payload.name || payload.email,
      email: payload.email,
      avatar: undefined,
    };
  },

  onError: async (error) => {
    // 🔴 401 ONLY. A 403 is "you may not see this" — a missing permission or a
    // WAF block — and the session is fine; until 2026-09-21 it signed the
    // operator out. See session-end.ts.
    if (errorEndsTheSession(error)) {
      sessionEndedByApiError = true;
      return {
        logout: true,
        // A 401 from our own API: a session existed and is over.
        redirectTo: sessionEndedLoginPath(window.location.pathname, window.location.search, true),
        error,
      };
    }
    return { error };
  },
};

/**
 * True when login() surfaced a NEW_PASSWORD_REQUIRED challenge that has not
 * yet been completed. Used by the LoginPage to decide whether the
 * "Set your new password" form is still actionable.
 */
export const hasPendingNewPasswordChallenge = (): boolean =>
  pendingChallengeUser !== null;

/**
 * Completes an in-flight Cognito NEW_PASSWORD_REQUIRED challenge for a
 * first-time invitee. On success the Cognito session is fully established
 * (authProvider.check() will pass) and the admin can proceed to /dashboard.
 */
export const completeNewPassword = async (
  newPassword: string
): Promise<{ success: boolean; error?: { name: string; message: string } }> => {
  if (!pendingChallengeUser) {
    return {
      success: false,
      error: {
        name: 'NoChallenge',
        message: 'No password challenge in progress. Please sign in again.',
      },
    };
  }
  const user = pendingChallengeUser;
  const email = pendingChallengeEmail || '';
  return new Promise<LoginResult>((resolve) => {
    // Pass {} for attributes — Cognito rejects immutable attrs on the challenge
    // response. Reuse the shared challenge handlers so that, on the admin pool,
    // a successful new-password chains straight into the MFA_SETUP challenge.
    user.completeNewPasswordChallenge(newPassword, {}, makeChallengeCallbacks(user, email, resolve));
  });
};

// ── TOTP MFA (admin-identity-plane Phase 1) ────────────────────────────────

/** True while an MFA_SETUP (TOTP enrollment) challenge is awaiting the user's
 *  first authenticator code. */
export const hasPendingMfaSetup = (): boolean =>
  pendingChallengeUser !== null && pendingMfaSecret !== null;

/** True while a SOFTWARE_TOKEN_MFA challenge is awaiting a TOTP code from a
 *  returning admin. */
export const hasPendingMfaCode = (): boolean =>
  pendingChallengeUser !== null && pendingMfaSecret === null && !hasPendingNewPasswordChallenge();

/** The TOTP secret + otpauth:// URI for the enrollment QR, or null. */
export const getMfaSetup = (): { secret: string; otpauthUri: string } | null =>
  pendingMfaSecret && pendingMfaOtpauthUri
    ? { secret: pendingMfaSecret, otpauthUri: pendingMfaOtpauthUri }
    : null;

/** Completes a TOTP enrollment: verifies the user's first code against the
 *  associated secret. On success the admin is fully authenticated. */
export const completeMfaSetup = async (totpCode: string): Promise<LoginResult> => {
  if (!pendingChallengeUser) {
    return { success: false, error: { name: 'NoChallenge', message: 'No MFA setup in progress. Please sign in again.' } };
  }
  const user = pendingChallengeUser;
  const email = pendingChallengeEmail || '';
  return new Promise<LoginResult>((resolve) => {
    user.verifySoftwareToken(totpCode.trim(), 'UDC Admin', {
      onSuccess: () => {
        // verifySoftwareToken authenticates + enforces TOTP on future logins,
        // but does NOT populate UserMFASettingList until SetUserMFAPreference is
        // called. Persist the preference so admin tooling / security audits
        // accurately report the admin as MFA-enrolled. Best-effort: the factor
        // is already enforced regardless, so we finish login either way.
        user.setUserMfaPreference(
          null,
          { PreferredMfa: true, Enabled: true },
          () => finishLogin(email, resolve),
        );
      },
      onFailure: (err: { message?: string }) =>
        resolve({ success: false, error: { name: 'MfaSetupError', message: err?.message || 'Invalid authenticator code' } }),
    });
  });
};

/** Submits a TOTP code for a returning admin's SOFTWARE_TOKEN_MFA challenge. */
export const submitMfaCode = async (totpCode: string): Promise<LoginResult> => {
  if (!pendingChallengeUser) {
    return { success: false, error: { name: 'NoChallenge', message: 'No MFA challenge in progress. Please sign in again.' } };
  }
  const user = pendingChallengeUser;
  const email = pendingChallengeEmail || '';
  return new Promise<LoginResult>((resolve) => {
    user.sendMFACode(totpCode.trim(), makeChallengeCallbacks(user, email, resolve), 'SOFTWARE_TOKEN_MFA');
  });
};
